/**
 * Client-side refinement of AML / PEP search hits (post-listing).
 * Keeps the provider search broad; filters locally for analyst review.
 */

export function tokenizeName(value) {
  return String(value || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .map((t) => t.trim())
    .filter(Boolean)
}

function nameHaystacks(match) {
  const names = [
    match?.primaryName,
    match?.name,
    match?.cardHeadline,
    match?.displayTitle,
    ...(Array.isArray(match?.aliases) ? match.aliases : []),
  ].filter(Boolean)
  return names.map((raw) => ({
    raw: String(raw),
    lower: String(raw).toLowerCase(),
    tokens: tokenizeName(raw),
  }))
}

/**
 * @param {'all'|'all_words'|'first_last'|'first_last_exact'} mode
 */
export function passesNameMatchMode(match, searchQuery, mode = 'all') {
  if (!mode || mode === 'all') return true
  const qTokens = tokenizeName(searchQuery)
  if (qTokens.length === 0) return true
  const haystacks = nameHaystacks(match)
  if (haystacks.length === 0) return false

  if (mode === 'all_words') {
    return haystacks.some((h) => qTokens.every((t) => h.tokens.includes(t) || h.lower.includes(t)))
  }

  const first = qTokens[0]
  const last = qTokens[qTokens.length - 1]

  if (mode === 'first_last') {
    // Primary given name + family name match; middle names allowed.
    return haystacks.some(
      (h) => h.tokens.length >= 2 && h.tokens[0] === first && h.tokens[h.tokens.length - 1] === last
    )
  }

  if (mode === 'first_last_exact') {
    // Exactly two name parts — drops "John Samuel William Higgs".
    return haystacks.some(
      (h) => h.tokens.length === 2 && h.tokens[0] === first && h.tokens[1] === last
    )
  }

  return true
}

/** Comma / newline / semicolon separated exclude phrases. */
export function parseExcludePhrases(raw) {
  return String(raw || '')
    .split(/[,;\n]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
}

export function passesExcludePhrases(match, excludePhrases) {
  if (!excludePhrases?.length) return true
  const blob = [
    match?.primaryName,
    match?.name,
    match?.cardHeadline,
    ...(Array.isArray(match?.aliases) ? match.aliases : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return !excludePhrases.some((phrase) => blob.includes(phrase))
}

export function passesMinConfidence(match, minConfidence) {
  const min = Number(minConfidence)
  if (!Number.isFinite(min) || min <= 0) return true
  const conf = Number(match?.confidence)
  if (!Number.isFinite(conf)) return true
  return conf >= min
}

/**
 * @param {object[]} matches
 * @param {{
 *   searchQuery?: string,
 *   nameMode?: 'all'|'all_words'|'first_last'|'first_last_exact',
 *   excludeText?: string,
 *   minConfidence?: number|string,
 *   hideAdverseMedia?: boolean,
 * }} filters
 */
export function filterAmlSearchResults(matches, filters = {}) {
  const list = Array.isArray(matches) ? matches : []
  const {
    searchQuery = '',
    nameMode = 'all',
    excludeText = '',
    minConfidence = 0,
    hideAdverseMedia = false,
  } = filters
  const excludePhrases = parseExcludePhrases(excludeText)

  return list.filter((m) => {
    if (hideAdverseMedia) {
      const cat = String(m?.riskCategory || '')
      if (/adverse|media/i.test(cat) || m?.matchKind === 'media_article') return false
    }
    if (!passesMinConfidence(m, minConfidence)) return false
    if (!passesExcludePhrases(m, excludePhrases)) return false
    if (!passesNameMatchMode(m, searchQuery, nameMode)) return false
    return true
  })
}

export function hasActiveAmlResultFilters(filters = {}) {
  return Boolean(
    (filters.nameMode && filters.nameMode !== 'all') ||
      String(filters.excludeText || '').trim() ||
      Number(filters.minConfidence) > 0 ||
      filters.hideAdverseMedia
  )
}
